import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { createFocusTrap } from "focus-trap";

const selectors = {
  accountComponent: "shopify-account",
  customerAccountButton: "[data-mobile-customer-account]",
  menuButton: "[data-menu-button]",
  closeButton: ".mobile-menu-close-button",
  overlay: "[data-overlay]",
  item: "[data-action]",
  viewportContent: ".mobile-menu__viewport-content",
  panes: "[data-pane-key]",
  homePane: '[data-pane-key="home"]',
};

const classes = {
  active: "active",
  belowHeader: "below-header",
};

const menuNav = node => {
  const overlay = node.querySelector(selectors.overlay);
  const menuButton = document.querySelector(selectors.menuButton);
  const closeButton = node.querySelector(selectors.closeButton);
  const viewportContentEl = node.querySelector(selectors.viewportContent);
  const homePane = node.querySelector(selectors.homePane);
  const items = node.querySelectorAll(selectors.item);
  const focusTrap = createFocusTrap(node, {
    allowOutsideClick: true,
    preventScroll: true,
    tabbableOptions: { getShadowRoot: true },
  });
  const customerAccountButton = node.querySelector(
    selectors.customerAccountButton
  );

  overlay.addEventListener("click", close);
  items.forEach(item => item.addEventListener("click", handleItemClick));

  // When the customer account icon includes a label, and the Shopify account component is being used, we want
  // the label to be "clickable", but there's no way to include it in the account component. With that, we need to listen
  // for clicks on the "button" as a whole and trigger a click on the account component's button. Fingers crossed Shopify
  // doesn't change the structure enough to break this.
  if (customerAccountButton) {
    customerAccountButton.addEventListener("click", event => {
      const accountComponent = event.currentTarget.querySelector(
        selectors.accountComponent
      );
      const clickEvent = new Event("click");
      const accountComponentButton =
        accountComponent.shadowRoot.querySelector("button");

      accountComponentButton.dispatchEvent(clickEvent);
    });
  }

  function handleItemClick(e) {
    const { action } = e.currentTarget.dataset;

    // Standard link that goes to a different url
    if (action === "link") return;
    e.preventDefault();

    switch (action) {
      // Element that will navigate to child navigation list
      case "show-sub-pane":
        handleParentClick(e);
        break;
      // Element that goes all the way back to home
      case "home":
        clickHome(e);
        break;
    }
  }

  function open() {
    node.classList.add(classes.active);
    document.body.setAttribute("mobile-menu-open", "true");
    menuButton.setAttribute("aria-expanded", true);
    menuButton.setAttribute(
      "aria-label",
      menuButton.getAttribute("data-aria-label-opened")
    );
    homePane.addEventListener("scroll", handleCloseButtonBelowHeader);

    setTimeout(() => {
      focusTrap.activate();

      disableBodyScroll(node, {
        hideBodyOverflow: true,
        allowTouchMove: el => {
          while (el && el !== document.body && el.id !== "main") {
            if (el.getAttribute("data-scroll-lock-ignore") !== null) {
              return true;
            }

            el = el.parentNode;
          }
        },
      });
    }, 50);
  }

  function close(e) {
    node.classList.remove(classes.active);

    menuButton.setAttribute("aria-expanded", false);
    menuButton.setAttribute(
      "aria-label",
      menuButton.getAttribute("data-aria-label-closed")
    );

    e && e.preventDefault();
    focusTrap.deactivate();

    document.body.setAttribute("mobile-menu-open", "false");

    setTimeout(() => {
      const panes = node.querySelectorAll(selectors.panes);

      panes.forEach(pane => {
        pane.scrollTo(0, 0);
      });

      paneIsBecomingVisible("home");
    }, 500);

    setTimeout(() => {
      enableBodyScroll(node);
      navigate(0);
    }, 250);
  }

  function handleCloseButtonBelowHeader(e) {
    const scrollPosition = e.target.scrollTop;

    if (scrollPosition > 9) {
      closeButton.classList.add(classes.belowHeader);
    } else {
      closeButton.classList.remove(classes.belowHeader);
    }
  }

  function paneIsBecomingVisible(key) {
    const panes = node.querySelectorAll(selectors.panes);

    panes.forEach(pane => {
      const visible = pane.dataset.paneKey === key;

      if (visible) {
        pane.removeAttribute("inert", false);
        pane.addEventListener("scroll", handleCloseButtonBelowHeader);

        setTimeout(() => {
          // This delay is necessary for an apparent quirk with the inert polyfill where it
          // incorrectly sets aria-hidden to true.
          pane.removeAttribute("aria-hidden", false);
        }, 10);
      } else {
        pane.setAttribute("inert", true);
        pane.setAttribute("aria-hidden", true);
        pane.removeEventListener("scroll", handleCloseButtonBelowHeader);
      }
    });
  }

  function handleParentClick(e) {
    e.preventDefault();

    const parentLink = e.currentTarget;
    const paneKey = parentLink.dataset.linkTargetPaneKey;

    navigateToPaneByKey(paneKey, { focusFirst: true });
    parentLink.ariaExpanded = "true";
  }

  function navigate(depth) {
    viewportContentEl.setAttribute("data-depth", depth);
  }

  function navigateToPaneByKey(paneKey, options = {}) {
    const pane = node.querySelector(`[data-pane-key="${paneKey}"]`);

    if (!pane) {
      return;
    }

    const depth = pane.dataset.menuDepth;

    paneIsBecomingVisible(paneKey);
    navigate(depth);

    if (options.focusFirst) {
      setTimeout(() => {
        const firstFocusable =
          pane.querySelector(".mobile-menu__link") ||
          pane.querySelector("input:not([type='hidden'])");
        firstFocusable?.focus();
      }, 500);
    }
  }

  function clickHome(e) {
    e.preventDefault();
    paneIsBecomingVisible("home");
    navigate(0);
  }

  function handleKeyboard(e) {
    if (e.key == "Escape" || e.keyCode === 27) {
      close();
    }
  }

  window.addEventListener("keydown", handleKeyboard);

  function destroy() {
    overlay.removeEventListener("click", close);
    items.forEach(item => item.removeEventListener("click", handleItemClick));
    enableBodyScroll(node);
    document.body.classList.remove("scroll-lock");
    document.body.style.top = "";
    window.removeEventListener("keydown", handleKeyboard);
  }

  return { close, destroy, open, navigateToPaneByKey };
};

export default menuNav;
